import { API } from "@swivl-mono/api-helper";
import { DateHelper } from "@swivl-mono/helpers";
import { TicketTemplate } from "@swivl-mono/types";
import React, { useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { TemplateEditorModal } from "./TemplateEditorModal";

export function TicketTemplatesPage() {
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<TicketTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.Ticketing.listTemplates(Member.orgId);
      setTemplates(res.templates);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
  }, []);

  const handleDelete = async (template: TicketTemplate) => {
    if (!confirm(`Are you sure you want to disable "${template.name}"?`)) return;
    try {
      await API.Ticketing.disableTemplate(Member.orgId, template.id);
      await loadTemplates();
    } catch (e: any) {
      alert(e?.message ?? "Failed to disable template");
    }
  };

  return (
    <div className="h-full overflow-auto p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Ticket Templates</h1>
          <p className="mt-1 text-sm text-neutral-500">Create and manage templates for common ticket types</p>
        </div>
      </div>

      {/* Error */}
      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm text-neutral-500">Loading templates...</div>
        </div>
      )}

      {/* Template grid */}
      {!loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* New template card */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex h-48 flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 transition hover:border-neutral-400 hover:bg-neutral-100"
            type="button"
          >
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-200">
              <svg className="h-6 w-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="text-sm font-medium text-neutral-600">New Template</span>
          </button>

          {/* Template cards */}
          {templates.map((template) => (
            <div
              key={template.id}
              className="relative flex h-48 flex-col rounded-xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:shadow-md"
            >
              {/* Status badges */}
              <div className="mb-2 flex gap-1.5">
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                    template.enabled ? "bg-green-50 text-green-700" : "bg-neutral-100 text-neutral-500"
                  ].join(" ")}
                >
                  {template.enabled ? "Enabled" : "Disabled"}
                </span>
                {template.isDefault && (
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Default</span>
                )}
              </div>

              {/* Name */}
              <h3 className="mb-1 text-lg font-semibold text-neutral-900">{template.name}</h3>

              {/* Meta */}
              <div className="mt-auto text-xs text-neutral-500">Updated {DateHelper.timeAgoShort(template.updatedAt as any)}</div>

              {/* Actions */}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setEditingTemplate(template)}
                  className="flex-1 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                  type="button"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(template)}
                  className="rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-500 hover:bg-red-50 hover:text-red-600"
                  type="button"
                  title="Disable template"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && templates.length === 0 && (
        <div className="mt-8 text-center">
          <svg className="mx-auto h-12 w-12 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-4 text-sm text-neutral-500">No templates yet. Create one to get started.</p>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <TemplateEditorModal
          template={null}
          allTemplates={templates}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            void loadTemplates();
          }}
        />
      )}

      {/* Edit modal */}
      {editingTemplate && (
        <TemplateEditorModal
          template={editingTemplate}
          allTemplates={templates}
          onClose={() => setEditingTemplate(null)}
          onSave={() => {
            setEditingTemplate(null);
            void loadTemplates();
          }}
        />
      )}
    </div>
  );
}
